import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Alert, Box, Button, Card, CardContent, FormControlLabel, Grid, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import { CloudUploadOutlined, DeleteOutline, ImageOutlined, SendOutlined } from '@mui/icons-material';
import { useChannelTest } from '@/hooks/useSetupGuide';
import { humanizeError } from '@/utils/errorMessage';
import { Controller, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { filesApi } from '@/api/files.api';
import Can from '@/components/common/Can';
import DataState from '@/components/common/DataState';
import { useNotify } from '@/components/common/useNotify';
import LoginPageSection from './LoginPageSection';
import {
  useBrandingSettings,
  useEmailSettings,
  useUpdateBrandingSettings,
  useUpdateEmailSettings,
  useUpdateWhatsAppSettings,
  useWhatsAppSettings,
} from '@/hooks/useSettings';
import type {
  UpsertBrandingSettingsRequest,
  UpsertEmailSettingsRequest,
  UpsertWhatsAppSettingsRequest,
} from '@/types/settings';

/** Compact card frame shared by every section: title + optional one-line description, then the body. */
function SectionCard({ title, description, children }: { title: string; description?: ReactNode; children: ReactNode }) {
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" component="h2" fontWeight={700}>{title}</Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{description}</Typography>
          )}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

/** The save row under a section form: right-aligned on desktop, full width on phones. */
function SaveRow({ children }: { children: ReactNode }) {
  return (
    <Can perm="Company.Edit">
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2.5, '& > *': { width: { xs: '100%', sm: 'auto' } } }}>
        {children}
      </Stack>
    </Can>
  );
}

/** Reusable image-asset manager: live preview + upload/replace + remove for a branding FileId. */
function BrandingAssetField({
  label, fileId, hint, accept = 'image/*', busy, onPick, onRemove,
}: {
  label: string; fileId?: number | null; hint?: string; accept?: string; busy: boolean;
  onPick: (file: File) => void; onRemove: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const blob = useQuery({
    queryKey: ['branding-asset', fileId],
    queryFn: () => filesApi.fetchBlob(fileId!),
    enabled: !!fileId,
    staleTime: Infinity,
    retry: false,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob.data) { setUrl(null); return; }
    const objectUrl = URL.createObjectURL(blob.data);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob.data]);

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      spacing={1.5}
      alignItems="center"
      sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}
    >
      <Box sx={{ width: 64, height: 64, borderRadius: 2, border: '1px dashed', borderColor: 'divider', display: 'grid', placeItems: 'center', bgcolor: 'action.hover', overflow: 'hidden', flexShrink: 0 }}>
        {url ? <Box component="img" src={url} alt={label} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
             : <ImageOutlined sx={{ color: 'text.disabled' }} />}
      </Box>
      <Box sx={{ flex: '1 1 160px', minWidth: 0 }}>
        <Typography variant="subtitle2">{label}</Typography>
        <Typography variant="caption" color="text.secondary">{fileId ? hint ?? 'Current image set.' : `No ${label.toLowerCase()} uploaded.`}</Typography>
      </Box>
      <Can perm="Company.Edit">
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0, ml: 'auto' }}>
          <input ref={inputRef} type="file" accept={accept} hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.currentTarget.value = ''; }} />
          <Button size="small" variant="outlined" startIcon={<CloudUploadOutlined />} disabled={busy} onClick={() => inputRef.current?.click()}>
            {fileId ? 'Replace' : 'Upload'}
          </Button>
          {fileId && (
            <Button size="small" color="error" startIcon={<DeleteOutline />} disabled={busy} onClick={onRemove}>Remove</Button>
          )}
        </Stack>
      </Can>
    </Stack>
  );
}

/** Branding — GET/PUT /api/branding-settings (+ logo/favicon via the Files API). */
export function BrandingSection() {
  const notify = useNotify();
  const query = useBrandingSettings();
  const update = useUpdateBrandingSettings();
  const [assetBusy, setAssetBusy] = useState(false);
  const { control, handleSubmit, reset, getValues, setValue } = useForm<UpsertBrandingSettingsRequest>();

  useEffect(() => {
    if (query.data) {
      const { id: _i, createdAt: _c, updatedAt: _u, ...rest } = query.data;
      reset(rest);
    }
  }, [query.data, reset]);

  const onSubmit = handleSubmit(async (v) => {
    try { await update.mutateAsync(v); notify.success('Branding saved.'); }
    catch (e) { notify.error(e); }
  });

  // Persist a logo/favicon change immediately (so it refreshes everywhere) without losing in-progress edits.
  const saveAsset = async (field: 'logoFileId' | 'faviconFileId', newId: number | null) => {
    const oldId = getValues(field) as number | null | undefined;
    setAssetBusy(true);
    try {
      await update.mutateAsync({ ...getValues(), [field]: newId } as UpsertBrandingSettingsRequest);
      setValue(field, newId as never);
      notify.success(newId ? 'Image updated.' : 'Image removed.');
      if (oldId && oldId !== newId) { try { await filesApi.remove(oldId); } catch { /* best-effort cleanup */ } }
    } catch (e) { notify.error(e); } finally { setAssetBusy(false); }
  };

  const pickAsset = async (field: 'logoFileId' | 'faviconFileId', file: File) => {
    setAssetBusy(true);
    try {
      const uploaded = await filesApi.upload(file, { entityType: 'branding', isPublic: true });
      await saveAsset(field, uploaded.id);
    } catch (e) { notify.error(e); setAssetBusy(false); }
  };

  return (
    <DataState loading={query.isLoading} error={query.error} onRetry={query.refetch} loadingText="Loading branding…">
      <Stack spacing={2}>
        <SectionCard
          title="Logo & favicon"
          description="Shown on the login screen, sidebar, header and customer documents. Changes apply instantly across the app."
        >
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 6 }}>
              <BrandingAssetField label="Company logo" fileId={query.data?.logoFileId} busy={assetBusy}
                hint="Used on login, sidebar, header, emails and PDFs, always whole. Best: PNG or SVG, at least 400 px wide, see-through background." accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onPick={(f) => pickAsset('logoFileId', f)} onRemove={() => saveAsset('logoFileId', null)} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <BrandingAssetField label="Favicon" fileId={query.data?.faviconFileId} busy={assetBusy}
                hint="Browser tab icon." accept="image/png,image/x-icon,image/svg+xml"
                onPick={(f) => pickAsset('faviconFileId', f)} onRemove={() => saveAsset('faviconFileId', null)} />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard title="Theme colors">
          <form onSubmit={onSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Controller name="primaryColor" control={control} render={({ field }) => <TextField {...field} value={field.value ?? '#4f46e5'} label="Primary color" fullWidth size="small" />} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Controller name="secondaryColor" control={control} render={({ field }) => <TextField {...field} value={field.value ?? '#06b6d4'} label="Secondary color" fullWidth size="small" />} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Controller name="accentColor" control={control} render={({ field }) => <TextField {...field} value={field.value ?? '#f59e0b'} label="Accent color" fullWidth size="small" />} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Controller name="themeMode" control={control} render={({ field }) => (
                  <TextField {...field} value={field.value ?? 'Light'} label="Theme mode" select fullWidth size="small">
                    {['Light', 'Dark', 'Auto'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </TextField>
                )} />
              </Grid>
              <Grid size={12}>
                <Controller name="customCss" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label="Custom CSS" fullWidth multiline minRows={4} size="small" />} />
              </Grid>
            </Grid>
            <SaveRow>
              <Button type="submit" variant="contained" disabled={update.isPending}>Save colors</Button>
            </SaveRow>
          </form>
        </SectionCard>

        <LoginPageSection current={query.data?.loginTemplate} />
      </Stack>
    </DataState>
  );
}

/**
 * Test message through the company's own email / WhatsApp (guided setup, step "Connect WhatsApp and email").
 * Sent from the SAVED settings, so it proves what quotes will actually go out through.
 */
function ChannelTestRow({ channel, enabled, label, placeholder }: {
  channel: 'email' | 'whatsapp'; enabled: boolean; label: string; placeholder: string;
}) {
  const { email, whatsApp } = useChannelTest();
  const mutation = channel === 'email' ? email : whatsApp;
  const [to, setTo] = useState('');
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const send = async () => {
    setResult(null);
    if (channel === 'whatsapp' && !to.trim()) {
      setResult({ ok: false, text: 'Enter the mobile number to send the test to.' });
      return;
    }
    try {
      const r = channel === 'email' ? await email.mutateAsync(to.trim() || undefined) : await whatsApp.mutateAsync(to.trim());
      setResult({ ok: true, text: r.message });
    } catch (e) {
      setResult({ ok: false, text: humanizeError(e) });
    }
  };

  return (
    <Can perm="Company.Edit">
      <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 2.5, pt: 2 }}>
        <Typography variant="subtitle2" fontWeight={700}>Test message</Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.25 }}>
          {enabled ? 'Sent through the settings saved above.' : 'Save the settings above with Enabled switched on, then send a test.'}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'flex-start' }}>
          <TextField
            size="small"
            fullWidth
            label={label}
            placeholder={placeholder}
            value={to}
            onChange={(e) => setTo(e.target.value)}
            disabled={!enabled}
            type={channel === 'email' ? 'email' : 'tel'}
          />
          <Button
            variant="outlined"
            startIcon={<SendOutlined />}
            onClick={send}
            disabled={!enabled || mutation.isPending}
            sx={{ flexShrink: 0, minHeight: 40 }}
          >
            {mutation.isPending ? 'Sending…' : 'Send test'}
          </Button>
        </Stack>
        {result && (
          <Alert severity={result.ok ? 'success' : 'error'} sx={{ mt: 1.25 }}>{result.text}</Alert>
        )}
      </Box>
    </Can>
  );
}

/** Email / SMTP — GET/PUT /api/email-settings (password write-only). */
export function EmailSection() {
  const notify = useNotify();
  const query = useEmailSettings();
  const update = useUpdateEmailSettings();
  const { control, handleSubmit, reset } = useForm<UpsertEmailSettingsRequest & { smtpPassword: string }>();

  useEffect(() => {
    if (query.data) {
      reset({
        smtpHost: query.data.smtpHost ?? '', smtpPort: query.data.smtpPort, smtpUsername: query.data.smtpUsername ?? '',
        smtpPassword: '', useSsl: query.data.useSsl, fromName: query.data.fromName ?? '', fromEmail: query.data.fromEmail ?? '',
        replyToEmail: query.data.replyToEmail ?? '', isEnabled: query.data.isEnabled,
      });
    }
  }, [query.data, reset]);

  const onSubmit = handleSubmit(async (v) => {
    try {
      await update.mutateAsync({ ...v, smtpPort: Number(v.smtpPort), smtpPassword: v.smtpPassword || null });
      notify.success('Email settings saved.');
    } catch (e) { notify.error(e); }
  });

  return (
    <DataState loading={query.isLoading} error={query.error} onRetry={query.refetch} loadingText="Loading email settings…">
      <SectionCard title="Email (SMTP)" description="The mail server this workspace sends its emails through.">
        <form onSubmit={onSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Controller name="smtpHost" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label="SMTP host" fullWidth size="small" />} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="smtpPort" control={control} render={({ field }) => <TextField {...field} value={field.value ?? 587} label="Port" type="number" fullWidth size="small" />} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="smtpUsername" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label="Username" fullWidth size="small" />} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="smtpPassword" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label="Password" type="password" fullWidth size="small" helperText={query.data?.hasPassword ? 'Leave blank to keep current' : 'No password set'} />} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="fromName" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label="From name" fullWidth size="small" />} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="fromEmail" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label="From email" fullWidth size="small" />} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="replyToEmail" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label="Reply-to email" fullWidth size="small" />} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ minHeight: 40, alignItems: 'center', columnGap: 2 }}>
                <Controller name="useSsl" control={control} render={({ field }) => <FormControlLabel control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Use SSL" />} />
                <Controller name="isEnabled" control={control} render={({ field }) => <FormControlLabel control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Enabled" />} />
              </Stack>
            </Grid>
          </Grid>
          <SaveRow>
            <Button type="submit" variant="contained" disabled={update.isPending}>Save email</Button>
          </SaveRow>
        </form>
        <ChannelTestRow
          channel="email"
          enabled={!!query.data?.isEnabled && !!query.data?.smtpHost && !!query.data?.fromEmail}
          label="Send test to"
          placeholder="Your email (leave blank for your sign-in email)"
        />
      </SectionCard>
    </DataState>
  );
}

/** WhatsApp — GET/PUT /api/whatsapp-settings (secrets write-only). */
export function WhatsAppSection() {
  const notify = useNotify();
  const query = useWhatsAppSettings();
  const update = useUpdateWhatsAppSettings();
  const { control, handleSubmit, reset } = useForm<UpsertWhatsAppSettingsRequest>();

  useEffect(() => {
    if (query.data) {
      reset({
        provider: query.data.provider ?? '', apiBaseUrl: query.data.apiBaseUrl ?? '', apiKey: '', accessToken: '', appSecret: '',
        phoneNumberId: query.data.phoneNumberId ?? '', businessAccountId: query.data.businessAccountId ?? '',
        senderPhoneNumber: query.data.senderPhoneNumber ?? '', webhookVerifyToken: query.data.webhookVerifyToken ?? '', isEnabled: query.data.isEnabled,
      });
    }
  }, [query.data, reset]);

  const onSubmit = handleSubmit(async (v) => {
    try {
      await update.mutateAsync({ ...v, apiKey: v.apiKey || null, accessToken: v.accessToken || null, appSecret: v.appSecret || null });
      notify.success('WhatsApp settings saved.');
    } catch (e) { notify.error(e); }
  });

  const field = (name: keyof UpsertWhatsAppSettingsRequest, label: string, type = 'text', helper?: string) => (
    <Grid size={{ xs: 12, sm: 6 }} key={name}>
      <Controller name={name} control={control} render={({ field }) => <TextField {...field} value={(field.value as string) ?? ''} label={label} type={type} fullWidth size="small" helperText={helper} />} />
    </Grid>
  );

  return (
    <DataState loading={query.isLoading} error={query.error} onRetry={query.refetch} loadingText="Loading WhatsApp settings…">
      <SectionCard title="WhatsApp" description="Business API credentials used to send and receive WhatsApp messages.">
        <form onSubmit={onSubmit} noValidate>
          <Grid container spacing={2}>
            {field('provider', 'Provider')}
            {field('apiBaseUrl', 'API base URL')}
            {field('apiKey', 'API key', 'password', query.data?.hasApiKey ? 'Leave blank to keep current' : 'Not set')}
            {field('accessToken', 'Access token', 'password', query.data?.hasAccessToken ? 'Leave blank to keep current' : 'Not set')}
            {field('phoneNumberId', 'Phone number ID')}
            {field('businessAccountId', 'Business account ID')}
            {field('senderPhoneNumber', 'Sender phone number')}
            {field('webhookVerifyToken', 'Webhook verify token', 'text', 'Used once, when Meta subscribes to the webhook')}
            {field('appSecret', 'Meta app secret', 'password',
              query.data?.hasAppSecret
                ? 'Leave blank to keep current'
                : 'Required — until this is set, incoming WhatsApp messages are rejected')}
            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <Controller name="isEnabled" control={control} render={({ field }) => <FormControlLabel control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />} label="Enabled" />} />
            </Grid>
          </Grid>
          <SaveRow>
            <Button type="submit" variant="contained" disabled={update.isPending}>Save WhatsApp</Button>
          </SaveRow>
        </form>
        <ChannelTestRow
          channel="whatsapp"
          enabled={!!query.data?.isEnabled && !!query.data?.phoneNumberId}
          label="Send test to"
          placeholder="Mobile with country code, e.g. +91 98765 43210"
        />
      </SectionCard>
    </DataState>
  );
}
